import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TrebbleButton from './TrebbleButton';
import TrebbleCard from './TrebbleCard';
import { TrebbleTitle } from './TrebbleTypography';
import TrebbleDivider from './Divider';
import TrebbleInput from './TrebbleInput';

export interface FolderOption {
  id: string;
  name: string;
  itemCount?: number;
  parentFolderId?: string | null;
}

interface TreeFolder extends FolderOption {
  children: TreeFolder[];
  depth: number;
}

function buildFolderTree(folders: FolderOption[]): TreeFolder[] {
  const map = new Map<string, TreeFolder>();
  const roots: TreeFolder[] = [];

  folders.forEach(f => {
    map.set(f.id, { ...f, children: [], depth: 0 });
  });

  folders.forEach(f => {
    const node = map.get(f.id)!;
    if (f.parentFolderId && map.has(f.parentFolderId)) {
      const parent = map.get(f.parentFolderId)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  function setDepths(nodes: TreeFolder[], depth: number) {
    nodes.forEach(n => {
      n.depth = depth;
      setDepths(n.children, depth + 1);
    });
  }
  setDepths(roots, 0);

  return roots;
}

function flattenTree(nodes: TreeFolder[]): TreeFolder[] {
  const result: TreeFolder[] = [];
  function walk(items: TreeFolder[]) {
    items.forEach(n => {
      result.push(n);
      walk(n.children);
    });
  }
  walk(nodes);
  return result;
}

interface MoveToFolderContentProps {
  folders: FolderOption[];
  currentFolderId?: string | null;
  onConfirm: (folderId: string | null) => Promise<void> | void;
  onCancel: () => void;
  ti18n?: unknown;
  useEditorTheme?: boolean;
}

const getI18n = (ti18n: unknown, key: string, fallback: string): string => {
  if (ti18n && window.getI18n) {
    return window.getI18n(ti18n, key);
  }
  return fallback;
};

const NO_FOLDER_ID = '__no_folder__';

/* ---------- Component ---------- */

export const MoveToFolderContent: React.FC<MoveToFolderContentProps> = ({
  folders,
  currentFolderId,
  onConfirm,
  onCancel,
  ti18n,
  useEditorTheme = false,
}) => {
  const resolvedTitle = getI18n(ti18n, 'MOVE_TO_FOLDER_TITLE', 'Move to folder');
  const [selectedId, setSelectedId] = useState<string>(currentFolderId ?? NO_FOLDER_ID);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedId(currentFolderId ?? NO_FOLDER_ID);
    setSearchQuery('');
    setIsLoading(false);

    const tree = buildFolderTree(folders);
    const idsWithChildren = new Set<string>();
    const collectParents = (nodes: TreeFolder[]) => {
      nodes.forEach(n => {
        if (n.children.length > 0) {
          idsWithChildren.add(n.id);
          collectParents(n.children);
        }
      });
    };
    collectParents(tree);
    setExpandedIds(idsWithChildren);
  }, [currentFolderId, folders]);

  const toggleExpand = useCallback((folderId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);
  const flatFolders = useMemo(() => {
    if (searchQuery.trim()) {
      return flattenTree(folderTree);
    }
    const result: TreeFolder[] = [];
    function walk(nodes: TreeFolder[]) {
      nodes.forEach(n => {
        result.push(n);
        if (expandedIds.has(n.id)) {
          walk(n.children);
        }
      });
    }
    walk(folderTree);
    return result;
  }, [folderTree, expandedIds, searchQuery]);

  const noFolderLabel = getI18n(ti18n, 'ROOT_NO_FOLDER', 'No folder');

  const filteredFolders = useMemo(() => {
    if (!searchQuery.trim()) return flatFolders;
    const q = searchQuery.trim().toLowerCase();
    return flatFolders.filter((f) => f.name.toLowerCase().includes(q));
  }, [flatFolders, searchQuery]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await onConfirm(selectedId === NO_FOLDER_ID ? null : selectedId);
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, selectedId]);

  const isCurrent = (folderId: string): boolean => {
    if (folderId === NO_FOLDER_ID) return !currentFolderId;
    return folderId === currentFolderId;
  };

  const renderFolderRow = (folderId: string, name: string, isNoFolder: boolean, depth: number = 0, hasChildren: boolean = false, isExpanded: boolean = false, itemCount?: number) => {
    const isSelected = selectedId === folderId;
    return (
      <li
        key={folderId}
        role="option"
        tabIndex={0}
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
        className={`flex items-center gap-2 cursor-pointer select-none transition-all duration-100 mb-px ${
          isSelected ? 'border border-solid' : 'border border-transparent'
        }`}
        style={{
          padding: '8px 10px',
          paddingLeft: 10 + depth * 20,
          ...(isSelected ? {
            borderColor: 'var(--trebble-primary-100)',
            background: 'var(--trebble-primary-15)',
          } : {}),
        }}
        onClick={() => setSelectedId(folderId)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedId(folderId);
          }
          if (hasChildren && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
            e.preventDefault();
            if (e.key === 'ArrowRight' && !isExpanded) toggleExpand(folderId);
            else if (e.key === 'ArrowLeft' && isExpanded) toggleExpand(folderId);
          }
        }}
      >
        {hasChildren ? (
          <span
            role="button"
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
            onClick={(e) => { e.stopPropagation(); toggleExpand(folderId); }}
            className="cursor-pointer w-4 inline-flex items-center justify-center text-[10px] shrink-0 text-gray-400"
          >
            {isExpanded ? '\u25BE' : '\u25B8'}
          </span>
        ) : (
          <span className="w-4 inline-flex shrink-0" />
        )}
        <span className={`text-base shrink-0 leading-none ${isNoFolder ? 'opacity-40' : ''}`}>
          <i className="pe-7s-folder" />
        </span>
        <span className={`text-[13px] font-medium flex-1 min-w-0 whitespace-nowrap overflow-hidden text-ellipsis ${isNoFolder ? 'text-gray-400' : ''}`}>
          {name}
        </span>
        {itemCount != null && (
          <span className="text-[11px] text-gray-400 shrink-0">{itemCount}</span>
        )}
        {isCurrent(folderId) && (
          <span className="w-1.5 h-1.5 shrink-0" style={{ background: 'var(--trebble-primary)' }} />
        )}
      </li>
    );
  };

  /* ---------- Render ---------- */

  return (
    <TrebbleCard
      useEditorTheme={useEditorTheme}
      className="modal_content"
    >
      <div className="flex flex-col">
        {/* Title */}
        <TrebbleTitle
          level={4}
          className="mb-0 shrink-0"
          disableDefaultColors={useEditorTheme}
        >
          {resolvedTitle}
        </TrebbleTitle>

        <TrebbleDivider useEditorTheme={useEditorTheme} style={{ margin: '12px 0 16px 0' }} />

        {/* Search input */}
        <div className="mb-3">
          <TrebbleInput
            placeholder={getI18n(ti18n, 'SEARCH_FOLDERS', 'Search folders...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            useEditorTheme={useEditorTheme}
            prefix={<i className="pe-7s-search" style={{ fontSize: 16 }} />}
          />
        </div>

        {/* Section header */}
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {getI18n(ti18n, 'ALL_FOLDERS', 'All Folders')}
          </span>
        </div>

        {/* Folder tree list */}
        <ul className="max-h-80 overflow-y-auto list-none m-0 p-0" role="listbox">
          {(() => {
            const trimmedQuery = searchQuery.trim();
            const showNoFolderOption =
              !trimmedQuery || noFolderLabel.toLowerCase().includes(trimmedQuery.toLowerCase());
            return (
              <>
                {/* "No folder" option */}
                {showNoFolderOption && renderFolderRow(NO_FOLDER_ID, noFolderLabel, true)}

                {/* Folder items */}
                {filteredFolders.map((folder) =>
                  renderFolderRow(
                    folder.id,
                    folder.name,
                    false,
                    folder.depth,
                    folder.children.length > 0,
                    expandedIds.has(folder.id),
                    folder.itemCount
                  )
                )}

                {/* Empty state — only when no folder matches AND the synthetic
                    "No folder" option is also filtered out. */}
                {filteredFolders.length === 0 && trimmedQuery && !showNoFolderOption && (
                  <li className="py-4 px-2.5 text-center text-[13px] text-gray-400">
                    {getI18n(ti18n, 'NO_FOLDERS_MATCHING', 'No folders matching')} &ldquo;{trimmedQuery}&rdquo;
                  </li>
                )}
              </>
            );
          })()}
        </ul>

        {/* Footer with buttons */}
        <div className="flex gap-3 justify-end pt-4 mt-2 shrink-0">
          <TrebbleButton
            onClick={onCancel}
            disabled={isLoading}
            useEditorTheme={useEditorTheme}
          >
            {getI18n(ti18n, 'CANCEL', 'Cancel')}
          </TrebbleButton>
          <TrebbleButton
            type="primary"
            onClick={handleConfirm}
            loading={isLoading}
            useEditorTheme={useEditorTheme}
          >
            {getI18n(ti18n, 'MOVE_HERE', 'Move here')}
          </TrebbleButton>
        </div>
      </div>
    </TrebbleCard>
  );
};

export default MoveToFolderContent;
